import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface CreateStaffData {
  first_name: string;
  last_name: string;
  email: string;
  role?: string;
  user_id?: string;
  profile_image_url?: string;
  title?: string;
  permissions?: string;
  phone?: string;
  address?: string;
  details?: string;
}

export const useStaff = () => {
  return useQuery({
    queryKey: ['staff'],
    queryFn: async () => {
      console.log("Fetching staff...");
      const { data, error } = await supabase
        .from('crm_staff')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching staff:", error);
        throw error;
      }
      
      console.log("Fetched staff:", data);
      return data || [];
    },
  });
};

export const useStaffList = () => {
  return useQuery({
    queryKey: ['staff-list'],
    queryFn: async () => {
      console.log("Fetching staff list...");
      const { data, error } = await supabase
        .from('crm_staff')
        .select('id, first_name, last_name, email')
        .order('first_name', { ascending: true });

      if (error) {
        console.error("Error fetching staff list:", error);
        throw error;
      }
      
      console.log("Fetched staff list:", data);
      return data || [];
    },
  });
};

export const useStaffMember = (staffId: string) => {
  return useQuery({
    queryKey: ['staff', staffId],
    queryFn: async () => {
      console.log("Fetching staff member:", staffId);
      const { data, error } = await supabase
        .from('crm_staff')
        .select('*')
        .eq('id', staffId)
        .single();

      if (error) {
        console.error("Error fetching staff member:", error);
        throw error;
      }
      
      console.log("Fetched staff member:", data);
      return data;
    },
    enabled: !!staffId,
  });
};

export const useStaffContacts = (staffId: string) => {
  return useQuery({
    queryKey: ['staff-contacts', staffId],
    queryFn: async () => {
      console.log("Fetching contacts for staff:", staffId);
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          organizations!fk_profiles_organization(id, name, company_id)
        `)
        .or(`internal_owner_id.eq.${staffId},external_owner_id.eq.${staffId}`)
        .not('first_name', 'is', null)
        .not('last_name', 'is', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching staff contacts:", error);
        throw error;
      }
      
      console.log("Fetched staff contacts:", data);
      return data || [];
    },
    enabled: !!staffId,
  });
};

export const useStaffDeals = (staffId: string) => {
  return useQuery({
    queryKey: ['staff-deals', staffId],
    queryFn: async () => {
      console.log("Fetching deals for staff:", staffId);
      const { data, error } = await supabase
        .from('crm_deals')
        .select(`
          *,
          contact:profiles!crm_deals_contact_id_fkey(first_name, last_name, email),
          organization:organizations(name, company_id),
          stage:crm_deal_stages(name, color)
        `)
        .or(`assigned_to_staff_id.eq.${staffId},created_by_staff_id.eq.${staffId}`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching staff deals:", error);
        throw error;
      }
      
      console.log("Fetched staff deals:", data);
      return data || [];
    },
    enabled: !!staffId,
  });
};

export const useStaffDealStats = (staffId: string) => {
  return useQuery({
    queryKey: ['staff-deal-stats', staffId],
    queryFn: async () => {
      console.log("Fetching deal stats for staff:", staffId);
      const { data, error } = await supabase
        .from('crm_deals')
        .select('value, total_cameras, total_images, stage:crm_deal_stages(name)')
        .or(`assigned_to_staff_id.eq.${staffId},created_by_staff_id.eq.${staffId}`);

      if (error) {
        console.error("Error fetching staff deal stats:", error);
        throw error;
      }
      
      const deals = data || [];
      
      // Group by stage type
      const opportunities = deals.filter(d => d.stage?.name === 'Opportunity');
      const proposals = deals.filter(d => d.stage?.name === 'Proposal');
      const wonDeals = deals.filter(d => d.stage?.name === 'Won');
      
      const calculateTotals = (dealsList: any[]) => ({
        revenue: dealsList.reduce((sum, deal) => sum + (deal.value || 0), 0),
        cameras: dealsList.reduce((sum, deal) => sum + (deal.total_cameras || 0), 0),
        images: dealsList.reduce((sum, deal) => sum + (deal.total_images || 0), 0),
      });

      return {
        opportunities: calculateTotals(opportunities),
        proposals: calculateTotals(proposals),
        deals: calculateTotals(wonDeals),
        total: calculateTotals(deals),
      };
    },
    enabled: !!staffId,
  });
};

export const useCreateStaff = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateStaffData & { existing_user_mode?: boolean }) => {
      console.log("Creating staff with data:", data);
      
      // Clean up empty strings for optional fields while preserving required fields
      const cleanedData: CreateStaffData = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        // Only include optional fields if they have values
        ...(data.role && data.role !== "" && { role: data.role }),
        ...(data.user_id && data.user_id !== "" && { user_id: data.user_id }),
        ...(data.profile_image_url && data.profile_image_url !== "" && { profile_image_url: data.profile_image_url }),
        ...(data.title && data.title !== "" && { title: data.title }),
        ...(data.permissions && data.permissions !== "" && { permissions: data.permissions }),
        ...(data.phone && data.phone !== "" && { phone: data.phone }),
        ...(data.address && data.address !== "" && { address: data.address }),
        ...(data.details && data.details !== "" && { details: data.details }),
      };
      
      console.log("Cleaned staff data:", cleanedData);
      
      const { data: staff, error } = await supabase
        .from('crm_staff')
        .insert(cleanedData)
        .select()
        .single();

      if (error) {
        console.error("Error inserting staff:", error);
        throw error;
      }
      
      console.log("Staff created successfully:", staff);

      // If this is existing user mode, update their user role
      if (data.existing_user_mode && data.user_id && data.permissions) {
        console.log("Updating user role for existing user:", data.user_id);
        
        // Get the default organization ID (Snapshot Live)
        const defaultOrgId = '6d1e0952-da74-4199-8b58-bd7929f5069f';
        
        // Update user role to match staff permissions
        const roleMapping: Record<string, 'standard' | 'admin' | 'superuser' | 'partner'> = {
          'staff': 'standard',
          'manager': 'admin', 
          'admin': 'admin',
          'superuser': 'superuser'
        };
        
        const userRole = roleMapping[data.permissions] || 'standard';
        
        const { error: roleError } = await supabase
          .from('user_roles')
          .upsert({
            user_id: data.user_id,
            organization_id: defaultOrgId,
            role: userRole
          }, {
            onConflict: 'user_id,organization_id'
          });

        if (roleError) {
          console.error("Error updating user role:", roleError);
          // Don't throw here - staff creation succeeded
        } else {
          console.log("User role updated successfully");
        }
      }
      
      return staff;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      toast({
        title: "Success",
        description: "Staff member created successfully",
      });
    },
    onError: (error: any) => {
      console.error("Error creating staff:", error);
      // Don't show toast here - let the component handle specific error messages
    },
  });
};

export const useUpdateStaff = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CreateStaffData> }) => {
      // Clean up empty strings for optional fields
      const cleanedData: Partial<CreateStaffData> = {};
      
      // Always include required fields if they exist
      if (data.first_name !== undefined) cleanedData.first_name = data.first_name;
      if (data.last_name !== undefined) cleanedData.last_name = data.last_name;
      if (data.email !== undefined) cleanedData.email = data.email;
      
      // Only include optional fields if they have values
      if (data.role && data.role !== "") cleanedData.role = data.role;
      if (data.user_id && data.user_id !== "") cleanedData.user_id = data.user_id;
      if (data.profile_image_url && data.profile_image_url !== "") cleanedData.profile_image_url = data.profile_image_url;
      if (data.title && data.title !== "") cleanedData.title = data.title;
      if (data.permissions && data.permissions !== "") cleanedData.permissions = data.permissions;
      if (data.phone && data.phone !== "") cleanedData.phone = data.phone;
      if (data.address && data.address !== "") cleanedData.address = data.address;
      if (data.details && data.details !== "") cleanedData.details = data.details;
      
      const { data: staff, error } = await supabase
        .from('crm_staff')
        .update(cleanedData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return staff;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      toast({
        title: "Success",
        description: "Staff member updated successfully",
      });
    },
    onError: (error: any) => {
      console.error("Error updating staff:", error);
      // Don't show toast here - let the component handle specific error messages
    },
  });
};

export const useDeleteStaff = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('crm_staff')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      toast({
        title: "Success",
        description: "Staff member deleted successfully",
      });
    },
    onError: (error) => {
      console.error("Error deleting staff:", error);
      toast({
        title: "Error",
        description: "Failed to delete staff member",
        variant: "destructive",
      });
    },
  });
};

export const useSyncSuperusersToStaff = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      console.log("Syncing superusers to staff...");
      
      const { data: superusers, error: roleError } = await supabase
        .from('user_roles')
        .select(`
          user_id,
          role
        `)
        .in('role', ['superuser', 'admin']);

      if (roleError) {
        console.error("Error fetching superusers:", roleError);
        throw roleError;
      }

      console.log("Found superusers:", superusers);

      for (const user of superusers || []) {
        // Get profile data separately
        const { data: profile } = await supabase
          .from('profiles')
          .select('username, avatar_url')
          .eq('user_id', user.user_id)
          .single();

        const { data: existingStaff } = await supabase
          .from('crm_staff')
          .select('id')
          .eq('user_id', user.user_id)
          .single();

        if (!existingStaff) {
          const staffData = {
            user_id: user.user_id,
            first_name: profile?.username || 'Unknown',
            last_name: '',
            email: 'admin@example.com',
            role: user.role,
            permissions: user.role,
            profile_image_url: profile?.avatar_url
          };

          const { error: insertError } = await supabase
            .from('crm_staff')
            .insert(staffData);

          if (insertError) {
            console.error("Error inserting staff:", insertError);
          }
        }
      }

      return superusers;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      toast({
        title: "Success",
        description: "Superusers synced to staff successfully",
      });
    },
    onError: (error) => {
      console.error("Error syncing superusers:", error);
      toast({
        title: "Error",
        description: "Failed to sync superusers to staff",
        variant: "destructive",
      });
    },
  });
};
